import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { ExportRow } from '@portal/shared/types/export.types';
import { ListingPageModel } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { TableData } from '@portal/shared/types/api-response.types';
import { DevicesPayload } from 'src/app/pages/equipment/devices/components/devices.models';
import { roundedDecimalSymbols } from '@portal/shared/utils/math.utils';

import { selectUnitOfMeasure } from '../../../consumption-analytics/store/consumption-analytics.reducer';
import { IndividualConsumptionTableAPIService } from '../individual-consumption-table-api.service';
import { ConsumptionAnalyticsFieldName, IndividualConsumptionsItem } from '../individual-consumption-table.types';
import { IndividualConsumptionTableActions } from './individual-consumption-table.actions';
import { individualConsumptionTableFeature } from './individual-consumption-table.reducer';
import { ExportUtilsService } from 'src/app/services/utility/export-utils.service';
import { ExportFacadeService } from 'src/app/services/utility/export-facade.service';
import { IndividualConsumptionsPayload } from './individual-consumption-table.state';

export const fetchTableDataTriggered$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.payloadUpdated),
      map(() => IndividualConsumptionTableActions.getGridData())
    ),
  { functional: true }
);

export const fetchTableData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), api = inject(IndividualConsumptionTableAPIService)) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.getGridData),
      withLatestFrom(store.select(individualConsumptionTableFeature.selectPayload)),
      filter(([, payload]) => payload !== null),
      exhaustMap(([, payload]) =>
        api.getListingData(payload!).pipe(
          map(({ items, totalCount }) =>
            IndividualConsumptionTableActions.getGridDataSuccess({
              data: items,
              total: totalCount
            })
          ),
          catchError((error: unknown) =>
            of(IndividualConsumptionTableActions.getGridDataFailure({ error: String(error) }))
          )
        )
      )
    ),
  { functional: true }
);

export const export$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    api = inject(IndividualConsumptionTableAPIService),
    exportUtils = inject(ExportUtilsService),
    exportFacade = inject(ExportFacadeService)
  ) =>
    actions$.pipe(
      ofType(IndividualConsumptionTableActions.exportFile),
      withLatestFrom(
        store.select(individualConsumptionTableFeature.selectTableData),
        store.select(individualConsumptionTableFeature.selectPayload),
        store.select(selectUnitOfMeasure)
      ),
      filter(([, , payload]) => payload !== null),
      switchMap(([action, { total }, payload, unitOfMeasureDef]) => {
        const { visibility, order, fileType } = action;
        const { model: unitOfMeasureModel, multiplier } = unitOfMeasureDef;
        const selectedDirectionFlowName: string | null =
          payload?.filters?.find(filter => filter.filterType === ConsumptionAnalyticsFieldName.FlowDirection)
            ?.filteredValues[0] ?? null;
        const listingPageModel = {
          dataSource: {
            getAll: (tableState: IndividualConsumptionsPayload) =>
              api.getListingData({ ...payload!, ...tableState }).pipe(
                map(({ items, totalCount }) => ({
                  data: items.map(item => ({
                    ...item,
                    totalValue: item.totalValue ? roundedDecimalSymbols(item.totalValue * multiplier) : item.totalValue,
                    averageValue: item.averageValue
                      ? roundedDecimalSymbols(item.averageValue * multiplier)
                      : item.averageValue,
                    minimumValue: item.minimumValue
                      ? roundedDecimalSymbols(item.minimumValue * multiplier)
                      : item.minimumValue,
                    maximumValue: item.maximumValue
                      ? roundedDecimalSymbols(item.maximumValue * multiplier)
                      : item.maximumValue
                  })),
                  total: totalCount
                }))
              ),
            total: total
          },
          exportConfig: {
            fileName: `Export-${selectedDirectionFlowName ? `${selectedDirectionFlowName}IndividualConsumption` : 'TotalIndividualConsumption'}`,
            mapData: ({ data }: TableData<IndividualConsumptionsItem>): ExportRow[] => {
              return data.map(item => {
                const unitOfMeasure = unitOfMeasureModel.selectedItem.text;
                return {
                  deviceSerialNumber: { title: 'SerialNumber', value: item.deviceSerialNumber },
                  accountNumber: {
                    title: 'AccountNumber',
                    value:
                      item.accountNumber && item.accountSubNumber
                        ? `${item.accountNumber} / ${item.accountSubNumber}`
                        : ''
                  },
                  commDeviceId: { title: 'CommDeviceID', value: item.commDeviceId },
                  port: { title: 'Port', value: item.port },
                  deviceModel: { title: 'Model', value: item.deviceModel },
                  totalValue: { title: `Total(${unitOfMeasure})`, value: item.totalValue },
                  averageValue: { title: `Average(${unitOfMeasure})`, value: item.averageValue },
                  maximumValue: { title: `Max(${unitOfMeasure})`, value: item.maximumValue },
                  minimumValue: { title: `Min(${unitOfMeasure})`, value: item.minimumValue },
                  serviceLocationAddress: { title: 'ServiceLocationAddress', value: item.serviceLocationAddress }
                };
              });
            }
          }
        } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;

        return exportUtils
          .getTableDataForExport(payload as DevicesPayload, listingPageModel as ListingPageModel, total)
          .pipe(
            tap(data =>
              exportFacade.exportRows(
                fileType,
                data as ExportRow[],
                visibility,
                order,
                listingPageModel.exportConfig!.fileName!
              )
            ),
            switchMap(() => of(IndividualConsumptionTableActions.exportFileSuccess())),
            catchError(err => of(IndividualConsumptionTableActions.exportFileFailure({ error: err?.err })))
          );
      })
    ),
  { functional: true, dispatch: true }
);
